import { Component, Element, Host, h, Prop, forceUpdate } from "@stencil/core";
import { SubscriptionInterface } from "../interface";
import { i18nProvider } from "@trumpf/ux.i18n";

@Component({
    tag: "ux-iws-software-updates-tooltip",
    styleUrl: "software-updates-tooltip.scss",
})
export class SoftwareUpdatesTooltip {
    @Element() element: HTMLUxIwsSoftwareUpdatesTooltipElement;
    private baseClass = "ux-iws-software-updates-tooltip";

    /** info object which is passed down to the component */
    @Prop() info: SubscriptionInterface | null = null;

    /** optional language for translations (mainly used for storybook) - otherwise language is automatically detected by ux.i18n */
    @Prop() language?: string = "";

    /** Tooltip link */
    @Prop() tooltipLink: string;

    private i18nSubscriberId: string;

    connectedCallback() {
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
    }

    disconnectedCallback() {
        i18nProvider.unsubscribe(this.i18nSubscriberId);
    }

    componentWillLoad() {
        if (this.language !== "") {
            i18nProvider.changeLanguage(this.language);
        }
    }

    private renderCheckmarkIcon = () => {
        return (
            <ux-icon
                class="ux-u-mr-xxxs"
                name="checkmark"
                fill="primary"
                size-l="16"
            ></ux-icon>
        );
    };

    private renderCrossIcon = () => {
        return (
            <ux-icon class="ux-u-mr-xxxs" name="cross" size-l="12"></ux-icon>
        );
    };

    private renderListItem = (type: string) => {
        const { t } = i18nProvider;
        const isAvailable =
            this.info.subscriptionDetails[`isAvailableFor${type}Updates`];
        const isSubscribed =
            this.info.subscriptionDetails[`isSubscribedFor${type}Updates`];
        const label = t(`softwareUpdates:updateTypes.${type}.label`);

        return isAvailable || isSubscribed ? (
            <div class={`${this.baseClass}__list`}>
                {isSubscribed
                    ? this.renderCheckmarkIcon()
                    : this.renderCrossIcon()}
                <div class="ux-text-normal">{label}</div>
            </div>
        ) : null;
    };

    render() {
        const { t } = i18nProvider;
        return (
            <Host class={this.baseClass}>
                <ux-icon name="info" class="ux-u-ml-xxxs"></ux-icon>
                <ux-tooltip
                    tooltip-target="ux-icon[name='info']"
                    align="bottom"
                >
                    {this.info.subscriptionStatus ===
                        "UpdatesPartiallySubscribed" ||
                    this.info.subscriptionStatus === "AllUpdatesSubscribed" ? (
                        <div class="ux-text-tiny ux-text-tiny--bold ux-u-text-uppercase ux-u-mb-xxxs">
                            {t(
                                `softwareUpdates:subscriptionStatus.${this.info.subscriptionStatus}.tooltip.headline`
                            )}
                        </div>
                    ) : (
                        <div class="ux-text-normal ux-u-mb-xxxs">
                            {t(
                                `softwareUpdates:subscriptionStatus.${this.info.subscriptionStatus}.tooltip.copy`
                            )}
                        </div>
                    )}
                    {(this.info.subscriptionStatus ===
                        "UpdatesPartiallySubscribed" ||
                        this.info.subscriptionStatus ===
                            "AllUpdatesSubscribed") && (
                        <div class="ux-u-mb-xxxs">
                            {this.renderListItem("Security")}
                            {this.renderListItem("Functional")}
                            {this.renderListItem("Force")}
                        </div>
                    )}
                    <a
                        href={this.tooltipLink}
                        target="_blank"
                        class="ux-text-link ux-text-link--green"
                    >
                        {t("softwareUpdates:learnMore")}
                        <ux-icon name="arrow-small-right" />
                    </a>
                </ux-tooltip>
            </Host>
        );
    }
}
